import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

const sections = [
  {
    n: "1",
    title: "Introduction",
    body: [
      'These Venue Terms of Service ("Terms") govern your access to and use of the Swingle platform ("Swingle", "we", "our" or "us").',
      'By creating a Swingle account or using our services, you ("Venue", "you" or "your") agree to be bound by these Terms.',
      "If you do not agree with these Terms, you must not use the Swingle platform.",
    ],
  },
  {
    n: "2",
    title: "About Swingle",
    body: [
      "Swingle is a software platform that enables venues and event organisers to:",
      {
        list: [
          "Sell digital event tickets;",
          "Sell digital memberships;",
          "Manage customer information;",
          "Send email communications to their customers; and",
          "Manage entry to events.",
        ],
      },
      "Swingle is a technology provider only.",
      "Swingle is not an event organiser, promoter, venue operator or entertainment provider.",
    ],
  },
  {
    n: "3",
    title: "Venue Responsibilities",
    body: [
      "You are solely responsible for:",
      {
        list: [
          "Your venue and its operation;",
          "Your events;",
          "Your staff;",
          "Compliance with all applicable laws and regulations;",
          "The accuracy of information published through Swingle;",
          "The products and services you sell;",
          "The conduct of your employees, contractors and representatives.",
        ],
      },
      "Swingle accepts no responsibility for the operation of your venue or the events you organise.",
    ],
  },
  {
    n: "4",
    title: "Prohibited Use",
    body: [
      "Swingle may only be used for lawful purposes.",
      "You agree that you will not use Swingle to:",
      {
        list: [
          "engage in unlawful activity;",
          "facilitate criminal activity;",
          "mislead customers;",
          "sell fraudulent or unauthorised tickets;",
          "distribute unlawful content;",
          "infringe intellectual property rights;",
          "send unsolicited communications in breach of applicable law; or",
          "use the platform in any manner that could damage Swingle or its reputation.",
        ],
      },
    ],
  },
  {
    n: "5",
    title: "Adult Entertainment Disclaimer",
    body: [
      "Swingle provides software that enables venues to sell digital memberships, tickets and communicate with customers.",
      "Swingle provides venues with the facility to sell tickets to allow individuals to meet potential mates, make friends, and engage in social activities that promote interaction and conversation.",
      "Swingle does not organise events, provide entertainment, employ performers or participate in the operation of venues.",
      "Swingle does not solicit, facilitate, encourage, promote or condone prostitution, sexual services, escort services, illegal adult entertainment or any form of unlawful sexual activity.",
      "The platform is not intended to be used for arranging, promoting or facilitating any paid sexual services either in person or remotely.",
      "Each Venue remains solely responsible for ensuring that its business activities comply with all applicable laws, licensing requirements and regulatory obligations.",
      "Any Venue found to be using Swingle to promote or facilitate unlawful activity may have its account suspended or terminated immediately.",
    ],
  },
  {
    n: "6",
    title: "Compliance with Laws",
    body: [
      "Venues must comply with all applicable:",
      {
        list: [
          "licensing laws;",
          "employment laws;",
          "health and safety requirements;",
          "consumer protection legislation;",
          "privacy laws;",
          "marketing regulations; and",
          "tax obligations.",
        ],
      },
      "You warrant that your business is legally entitled to operate within the jurisdictions in which you use Swingle.",
    ],
  },
  {
    n: "7",
    title: "Customer Data",
    body: [
      "You acknowledge that:",
      {
        list: [
          "you are the Data Controller of your customer information;",
          "Swingle acts as your Data Processor when processing customer information on your behalf;",
          "you are responsible for obtaining any required consents for marketing communications;",
          "you are responsible for complying with applicable data protection legislation.",
        ],
      },
    ],
  },
  {
    n: "8",
    title: "Payments & Refunds",
    body: [
      "Payments made through Swingle are processed by third-party payment providers.",
      "Swingle does not hold customer funds unless expressly stated otherwise.",
      "You are responsible for ensuring that your payment account remains active and compliant with the requirements of the payment provider.",
      { heading: "Refunds (Consumer Rights Act 2015)" },
      { subheading: "Cancellations" },
      "Venues must offer all ticket holders a refund in the circumstance of event cancellation. Payments should be returned to ticket holders within 14 days. The refund amount must be no less than the face value of the ticket. If refunds are not issued, Swingle and/or its payment processor reserve the right to claw back funds from the Venue.",
      { subheading: "Rescheduling" },
      "If an event is rescheduled, Venues must offer all ticket holders a ticket on the new date at the new time. If the ticket holder cannot make the rescheduled event, they must be offered a refund of no less than the face value of their ticket.",
      { subheading: "Change of Mind" },
      "Ticket holders have no automatic legal right to a refund if they change their mind or can no longer attend an event that is still going ahead. Refunds in this circumstance remain at the discretion of the Venue.",
    ],
  },
  {
    n: "9",
    title: "Email Marketing",
    body: [
      "Where you use Swingle's email marketing functionality, you agree that:",
      {
        list: [
          "you have an appropriate lawful basis to contact recipients;",
          "you will honour unsubscribe requests;",
          "you will not send spam;",
          "you will comply with all applicable electronic marketing laws.",
        ],
      },
      "Swingle reserves the right to suspend email functionality where abuse or excessive complaints are identified.",
    ],
  },
  {
    n: "10",
    title: "Account Suspension",
    body: [
      "Swingle may suspend or terminate your account immediately where we reasonably believe that:",
      {
        list: [
          "these Terms have been breached;",
          "unlawful activity is taking place;",
          "your use creates legal or reputational risk;",
          "required payment obligations have not been met;",
          "regulatory authorities require us to do so.",
        ],
      },
    ],
  },
  {
    n: "11",
    title: "Intellectual Property",
    body: [
      "All software, branding, trademarks and intellectual property belonging to Swingle remain the exclusive property of Swingle.",
      "These Terms do not transfer ownership of any intellectual property.",
    ],
  },
  {
    n: "12",
    title: "Limitation of Liability",
    body: [
      'Swingle provides its services on an "as available" basis.',
      "To the fullest extent permitted by law, Swingle shall not be liable for:",
      {
        list: [
          "loss of profits;",
          "loss of revenue;",
          "loss of business;",
          "reputational damage;",
          "customer disputes;",
          "cancelled events;",
          "acts or omissions of the Venue;",
          "any indirect or consequential loss.",
        ],
      },
      "Nothing in these Terms limits liability where it cannot lawfully be excluded.",
    ],
  },
  {
    n: "13",
    title: "Indemnity",
    body: [
      "You agree to indemnify and keep indemnified Swingle against any claims, losses, liabilities, costs or expenses arising from:",
      {
        list: [
          "your events;",
          "your venue;",
          "your breach of these Terms;",
          "your breach of applicable laws;",
          "customer complaints relating to your business; or",
          "any unlawful activities carried out by you or on your behalf.",
        ],
      },
    ],
  },
  {
    n: "14",
    title: "Changes to the Platform",
    body: [
      "We may modify, improve or discontinue parts of the Swingle platform from time to time.",
      "Where practical, we will provide reasonable notice of material changes.",
    ],
  },
  {
    n: "15",
    title: "Changes to these Terms",
    body: [
      "We may update these Terms periodically.",
      "The latest version will always be available through the Swingle website.",
      "Continued use of the platform constitutes acceptance of the updated Terms.",
    ],
  },
  {
    n: "16",
    title: "Governing Law",
    body: [
      "These Terms shall be governed by the laws of England and Wales.",
      "Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
    ],
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-black text-[#F5F5F0]" data-testid="terms-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" data-testid="terms-home-link" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Swingle" className="h-8 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-6">
            Legal
          </p>
          <h1
            data-testid="terms-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
          >
            Swingle Venue
            <br />
            <span className="italic text-[#D9A6F5]">Terms of Service</span>
          </h1>
          <p className="mt-8 text-sm uppercase tracking-[0.18em] text-[#A3A3A3]">
            Effective Date: 14th July 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-14">
          {sections.map((s) => (
            <article key={s.n} data-testid={`terms-section-${s.n}`} className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10">
              <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]/70 pt-1">
                {s.n.padStart(2, "0")}
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-normal text-[#F5F5F0] mb-4">
                  {s.title}
                </h2>
                <div className="space-y-4 text-sm md:text-base text-[#C9C9C0] leading-relaxed">
                  {(() => {
                    // Number top-level paragraphs as {section}.1, {section}.2 …
                    // Reset numbering to letters under {heading} blocks (5.a, 5.b …)
                    let paraCount = 0;
                    let subCount = 0;
                    let inSub = false;
                    return s.body.map((item, i) => {
                      if (typeof item === "object" && item.heading) {
                        inSub = true;
                        subCount = 0;
                        return (
                          <h3
                            key={i}
                            className="text-[#F5F5F0] text-base md:text-lg font-normal pt-4 border-t border-white/10"
                          >
                            {item.heading}
                          </h3>
                        );
                      }
                      if (typeof item === "object" && item.subheading) {
                        return (
                          <p
                            key={i}
                            className="text-[#F5F5F0] uppercase tracking-[0.22em] text-[11px] pt-2"
                          >
                            {item.subheading}
                          </p>
                        );
                      }
                      if (typeof item === "string") {
                        let label;
                        if (inSub) {
                          subCount += 1;
                          label = `${s.n}.${String.fromCharCode(96 + subCount)}`;
                        } else {
                          paraCount += 1;
                          label = `${s.n}.${paraCount}`;
                        }
                        return (
                          <p key={i} className="flex gap-3">
                            <span className="text-[#A3A3A3]/70 flex-shrink-0 min-w-[38px] text-[#D9A6F5]/80">
                              {label}
                            </span>
                            <span>{item}</span>
                          </p>
                        );
                      }
                      return (
                        <ul key={i} className="list-none space-y-2 pl-0 md:pl-[38px]">
                          {item.list.map((li, j) => (
                            <li key={j} className="flex gap-3">
                              <span className="text-[#D9A6F5] mt-2 inline-block h-px w-3 flex-shrink-0" />
                              <span>{li}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    });
                  })()}
                </div>
              </div>
            </article>
          ))}

          {/* Contact section */}
          <article data-testid="terms-section-17" className="grid md:grid-cols-[80px_1fr] gap-4 md:gap-10 pt-4 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]/70 pt-8">
              17
            </div>
            <div className="pt-8">
              <h2 className="text-lg md:text-xl font-normal text-[#F5F5F0] mb-4">
                Contact
              </h2>
              <div className="space-y-2 text-sm md:text-base text-[#C9C9C0] leading-relaxed">
                <p>If you have any questions regarding these Terms, please contact:</p>
                <p className="pt-2">
                  <span className="block text-[#F5F5F0]">Swingle</span>
                  <span className="block">
                    Email:{" "}
                    <a
                      href="mailto:legal@swingleapp.com"
                      className="text-[#D9A6F5] hover:underline"
                      data-testid="terms-contact-email"
                    >
                      legal@swingleapp.com
                    </a>
                  </span>
                  <span className="block">
                    Website:{" "}
                    <a
                      href="https://www.swingleforvenues.com"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#D9A6F5] hover:underline"
                    >
                      www.swingleforvenues.com
                    </a>
                  </span>
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#A3A3A3] uppercase tracking-[0.18em]">
          <p>© {new Date().getFullYear()} Swingle For Venues.</p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              data-testid="terms-footer-privacy-link"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/"
              data-testid="terms-footer-home-link"
              className="text-[#A3A3A3]/70 hover:text-[#D9A6F5] transition-colors"
            >
              Return home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
